import { useEffect, useState } from "react";
import { CalendarClock, MapPin, BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import { Loading, PageTitle, Empty, Stat } from "@/components/Common";
import { HorarioSemanal, DIAS, colorDe } from "@/components/HorarioSemanal";
import { Badge } from "@/components/ui/badge";

const minutos = (h) => Number(h.split(":")[0]) * 60 + Number(h.split(":")[1]);

export default function DocenteHorario() {
  const [bloques, setBloques] = useState(null);
  const [materias, setMaterias] = useState([]);

  useEffect(() => {
    api.get("/horarios").then((r) => setBloques(r.data)).catch(() => setBloques([]));
    api.get("/docente/materias").then((r) => setMaterias(r.data));
  }, []);

  if (!bloques) return <Loading />;

  const hoy = (new Date().getDay() + 6) % 7;
  const deHoy = bloques.filter((b) => b.dia === hoy).sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio));
  const horas = bloques.reduce((t, b) => t + (minutos(b.hora_fin) - minutos(b.hora_inicio)) / 60, 0);
  const conSubtitulo = bloques.map((b) => ({
    ...b, subtitulo: [b.grado, b.aula].filter(Boolean).join(" · "),
  }));
  const usaSabado = bloques.some((b) => b.dia === 5);

  return (
    <div data-testid="docente-horario">
      <PageTitle title="Mi horario de clases"
                 subtitle="Distribución semanal de las materias que tiene asignadas." />

      <div className="grid gap-4 sm:grid-cols-3" data-testid="horario-docente-stats">
        <Stat label="Clases hoy" value={String(deHoy.length)} icon={CalendarClock} />
        <Stat label="Horas semanales" value={`${horas.toFixed(1)} h`} />
        <Stat label="Materias asignadas" value={String(materias.length)} icon={BookOpen} />
      </div>

      {bloques.length === 0 ? (
        <div className="mt-8">
          <Empty testId="horario-docente-vacio">
            La administración todavía no ha publicado su horario de clases.
          </Empty>
        </div>
      ) : (
        <>
          <section className="mt-8">
            <h2 className="font-display text-lg font-semibold">Hoy · {DIAS[hoy] || "Domingo"}</h2>
            {deHoy.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">No tiene clases programadas para hoy.</p>
            ) : (
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="clases-hoy">
                {deHoy.map((b) => (
                  <article key={b.id} className={`lift rounded-lg border p-5 ${colorDe(b.materia_nombre)}`}>
                    <p className="font-mono text-xs opacity-80">{b.hora_inicio} – {b.hora_fin}</p>
                    <h3 className="mt-1 font-display text-base font-bold">{b.materia_nombre}</h3>
                    <p className="mt-1 text-xs opacity-90">{b.grado}</p>
                    {b.aula && (
                      <p className="mt-2 flex items-center gap-1 text-xs opacity-90">
                        <MapPin className="h-3 w-3" /> {b.aula}
                      </p>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="mt-10">
            <h2 className="font-display text-lg font-semibold">Semana completa</h2>
            <div className="mt-4">
              <HorarioSemanal bloques={conSubtitulo} dias={usaSabado ? 6 : 5} diaResaltado={hoy} />
            </div>
          </section>
        </>
      )}

      {materias.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-lg font-semibold">Materias asignadas</h2>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="materias-asignadas">
            {materias.map((m) => (
              <Badge key={m.id} variant="outline" className="px-3 py-1.5 text-xs">
                {m.nombre} · {m.grado}
              </Badge>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
